'use client';
import FormWrapper from './formWrapper';
import styles from './overlays.module.css';
import {
    Button,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormGroup,
    FormControl,
    FormControlLabel,
    TextField,
    Select,
    InputLabel,
    MenuItem,
    SelectChangeEvent,
    Switch
} from '@mui/material';
import { useState } from 'react';
import Parse from 'html-react-parser';
import ModalClose from '@/components/ModalClose';
import { sendGAEvent } from '@next/third-parties/google';

export default function ApplyForm({
    isActive,
    onClose,
    email,
    onEmailChange
}: Readonly<{
    isActive: boolean;
    onClose: () => void;
    email: string;
    onEmailChange: (value: string) => void;
}>) {

    const [investorType, setInvestorType] = useState('');

    const handleInvestorTypeChange = (event: SelectChangeEvent) => {
        setInvestorType(event.target.value as string);
    };

    const [isError, setIsError] = useState(false);

    const [errorMessage, setErrorMessage] = useState('');

    const [userDetails, setUserDetails] = useState({});

    const [isSubmitted, setIsSubmitted] = useState(false);

    const [submittedModalChildren, setSubmittedModalChildren] = useState(<></>);

    const emailVerify = async () => {
        document.getElementById('apply-form-email-verify')!.parentElement!.innerHTML = 'Still no email? <a href="mailto:support@apexsignals.io">Contact support</a>.';
        await fetch('/api/sendVerificationEmail', {
            method: 'POST',
            body: JSON.stringify({ user_data: userDetails }),
            headers: {
                'content-type': 'application/json'
            }
        });
        return;
    }

    const handleClose = (responseData: object) => {
        setUserDetails(responseData);
        const { email: submittedEmail } = responseData as { email: string };
        setSubmittedModalChildren(
            <>
                <DialogContentText className={styles.modalDescription}>
                    {`Thanks for signing up! We've sent a verification email to ${submittedEmail}. Click the link in the email to verify your account and get started.`}
                </DialogContentText>
                <DialogContentText className={styles.modalDescription}>
                    <b>{"Didn’t receive the email?"}</b>
                </DialogContentText>
                <ol className={styles.modalList}>
                    <li>Check your spam or junk folder.</li>
                    <li>Wait a few minutes—it might take some time to arrive.</li>
                    <li>Still no email? <a id="apply-form-email-verify" className={styles.actionLink} onClick={() => emailVerify()}>Resend verification email</a>.</li>
                </ol>
            </>
            )
        setIsSubmitted(true);
        return;
    }

    const onSubmit = (handleClose: (responseData: object) => void) => async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const submitButton = document.getElementById('apply-form-submit') as HTMLElement;
        const cancelButton = document.getElementById('apply-form-cancel') as HTMLElement;
        for (const button of [submitButton, cancelButton]) {
            button.setAttribute('disabled', '');
            button.classList.toggle('Mui-disabled');
        }
        submitButton.innerText = 'Submitting...';
        const formData = new FormData(event.currentTarget);
        const formDataEntries = Array.from(formData.entries());
        const formJson = Object.fromEntries(formDataEntries) as Record<string, string>;
        const { email, first_name, last_name, ...questions } = formJson;

        const pageUtmSource = new URL(window.location.href).searchParams.get('utm_source');
        if (pageUtmSource) {
            questions['utm_source'] = pageUtmSource;
        }
        const pageUtmCampaign = new URL(window.location.href).searchParams.get('utm_campaign');
        if (pageUtmCampaign) {
            questions['utm_campaign'] = pageUtmCampaign;
        }

        if (!email.match(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)) {
            const responseData = { error: 'Please enter a valid email address' };
            setErrorMessage(responseData.error);
            setIsError(true);
            handleClose(responseData);
            return;
        }

        const signupResult = await fetch('/api/waitlistSignups', {
            method: 'POST',
            body: JSON.stringify({
                email: email.toLowerCase(),
                first_name: `${first_name.charAt(0).toUpperCase() + first_name.slice(1).toLowerCase()}`,
                last_name: `${last_name.charAt(0).toUpperCase() + last_name.slice(1).toLowerCase()}`,
                questions: JSON.stringify(questions),
                type: 'beta'
            }),
            headers: { 'content-type': 'application/json' }
        });

        let responseData: {
            [key: string]: string;
        } = { error: 'Something went wrong, please try again' };

        try {
            responseData = await signupResult.json();
        } catch {
        }

        if (signupResult.status === 201) {
            sendGAEvent('event', 'signup', {
                method: 'Beta sign up form'
            });
            handleClose(responseData);
        } else {
            submitButton.innerText = 'Submit';
            for (const button of [submitButton, cancelButton]) {
                button.removeAttribute('disabled');
                button.classList.toggle('Mui-disabled');
            }
            setErrorMessage(responseData.error);
            setIsError(true);
        }
    }

    return (
        <>
            <FormWrapper
                isActive={isActive}
                onClose={onClose}
                onSubmit={onSubmit(handleClose)}
                isError={isError}
                onErrorDialogClose={() => setIsError(false)}
                errorMessage={errorMessage}
                isSubmitted={isSubmitted}
                submittedDialogTitle="Confirm your email address"
                onSubmittedDialogClose={() => {
                    setIsSubmitted(false);
                    onClose();
                }}
                submittedChildren={submittedModalChildren}
            >
                <div className={styles.applyFormModal}>
                    <div className={styles.modalHeader}>
                        <DialogTitle id="modal-title" className={styles.modalTitle}>
                            Join the Apex Signals Beta
                        </DialogTitle>
                        <ModalClose onClick={onClose} />
                    </div>
                    <DialogContent>
                        <DialogContentText className={styles.modalDescription}>
                            Be one of the first to try Apex Signals, the next generation stock advisor API that helps you make data-driven trading decisions. Sign up below for exclusive early access and help shape the future of AI-powered investing!
                        </DialogContentText>
                        <div className={styles.formQuestions}>
                            <FormGroup>
                                <div className={styles.formFieldRow}>
                                    <FormControl>
                                        <TextField
                                            name="first_name"
                                            label="First name"
                                            placeholder="Your first name"
                                            autoComplete='off'
                                            required
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <TextField
                                            name="last_name"
                                            label="Last name"
                                            placeholder="Your last name"
                                            required
                                        />
                                    </FormControl>
                                </div>
                                <FormControl>
                                    <TextField
                                        name="email"
                                        label="Email"
                                        placeholder="Your email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => onEmailChange(e.target.value)}
                                        required
                                    />
                                </FormControl>
                                <FormControl className={styles.selectFormControl}>
                                    <InputLabel
                                        id="investor-type-label"
                                        className={styles.selectFieldLabel}
                                        required
                                    >
                                        How do you invest currently?
                                    </InputLabel>
                                    <Select
                                        labelId="investor-type-label"
                                        id="investor-type"
                                        name="investor_type"
                                        label="How do you invest currently?"
                                        onChange={handleInvestorTypeChange}
                                        value={investorType}
                                        required
                                    >
                                        <MenuItem value={'manual_stock_picks'}>I perform my own stock research and manually pick stocks</MenuItem>
                                        <MenuItem value={'advisor_stock_picks'}>I subscribe to a stock advisory service and I pick stocks based on their recommendations</MenuItem>
                                        <MenuItem value={'copy_trader'}>I subscribe to a copy trading strategy and stocks are automatically chosen and traded for me</MenuItem>
                                        <MenuItem value={'algo_trader'}>I build my own trading algorithms and use APIs to execute the trades (for instance in Metatrader)</MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl>
                                    <TextField
                                        name="trading_platforms"
                                        label="What trading platform(s) do you currently use?"
                                        placeholder="E.g. Robinhood, TD Ameritrade, Etoro, Interactive Brokers"
                                        required
                                    />
                                </FormControl>
                                <FormControl>
                                    <TextField
                                        name="automation_platforms"
                                        label="Do you already have automated investing strategies in place? If so which APIs, Metatrader platforms or other are you using?"
                                        placeholder="E.g. IG Metatrader, Interactive Brokers API"
                                    />
                                </FormControl>
                                <FormControl>
                                    <TextField
                                        name="stock_advisors"
                                        label="What stock advisors do you currently use?"
                                        placeholder="E.g. Zacks Research, The Motley Fool, Seeking Alpha, or None"
                                        required
                                    />
                                </FormControl>
                                <FormControl>
                                    <TextField
                                        name="why_apex"
                                        label="What interests you most about Apex Signals?"
                                        multiline
                                        minRows={2}
                                    />
                                </FormControl>
                            </FormGroup>
                            <FormGroup>
                                <FormControlLabel
                                    label="I would like to opt in to a short feedback call with the founding team"
                                    name="opt_in_to_feedback_call"
                                    control={<Switch />}
                                />
                                <FormControlLabel
                                    label={Parse('I agree to the collection and processing of my data in accordance with the <a href="/privacy">Privacy Policy</a>')}
                                    name="data_consent"
                                    control={<Switch />}
                                    required
                                />
                            </FormGroup>
                        </div>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            id="apply-form-cancel"
                            variant="outlined"
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            id="apply-form-submit"
                            variant="contained"
                            type="submit"
                        >
                            Submit
                        </Button>
                    </DialogActions>
                </div>
            </FormWrapper>
        </>
    );
}